import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { TinyHouseModel } from '@/types'

import Link from 'next/link'
import { ArrowDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SectionHeading } from '@/components/generals/section-heading'
import { PageHeader } from '@/components/landing/page-header'
import { ModelCard } from '@/components/landing/model-card'
import { CompareTable } from '@/components/landing/compare-table'
import { CtaBanner } from '@/components/landing/cta-banner'
import { buttonVariants } from '@/components/ui/button'
import { getModels } from '@/lib/content'
import { INCLUDED } from '@/constants/content'

interface ModelsPageProps {
    models: TinyHouseModel[]
}

const ModelsPage: FC<ModelsPageProps> = ({ models }): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Our models"
                description="Explore our handcrafted tiny houses on wheels and compare sizes, layouts and prices side by side, from compact retreats to family homes."
            />

            <PageHeader
                eyebrow="Our models"
                title="Homes made to measure"
                description="Every Roamwood model is a proven starting point. Pick the size that suits your life, then make it yours with your own layout, materials and options."
            >
                <div className='mt-9 flex flex-wrap gap-3'>
                    <Link href='#compare' className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                        Compare models
                        <ArrowDown className='size-4' />
                    </Link>

                    <Link href='/contact?model=custom' className={buttonVariants({ variant: 'ghost', size: 'lg' })}>
                        Need something custom?
                    </Link>
                </div>
            </PageHeader>

            <section id='model-list' aria-label="Tiny house models" className='py-16 lg:py-24'>
                <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                    {models.length > 0 ? (
                        <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
                            {models.map(model => (
                                <ModelCard key={model.id} model={model} headingLevel='h2' />
                            ))}
                        </div>
                    ) : (
                        <div className='rounded-2xl border border-dashed border-border bg-card px-6 py-16 text-center'>
                            <h2 className='font-heading text-2xl'>
                                Our models are being updated
                            </h2>

                            <p className='mx-auto mt-3 max-w-md text-muted-foreground'>
                                Tell us what you are looking for and we will send you the latest designs and prices.
                            </p>

                            <Link href='/contact' className={cn(buttonVariants(), 'mt-6')}>
                                Get in touch
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            {models.length > 0 && (
                <section id='compare' className='border-y border-border/70 bg-secondary/50 py-16 lg:py-24'>
                    <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                        <SectionHeading
                            eyebrow="Side by side"
                            title="Compare our models"
                            description="The key numbers at a glance. Every model can be adapted, so treat these as a starting point rather than a limit."
                        />

                        <div className='mt-12'>
                            <CompareTable models={models} />
                        </div>

                        <p className='mt-5 text-sm text-muted-foreground'>
                            Prices are starting prices before options, delivery and site connections. Your quote is fixed once your design is agreed.
                        </p>
                    </div>
                </section>
            )}

            <section id='included' className='py-16 lg:py-24'>
                <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <SectionHeading
                            eyebrow="Included as standard"
                            title="A complete home, not a shell"
                            description="Every model leaves our workshop ready to live in. Here is what comes with every house before you add a single option."
                        />
                    </div>

                    <ul className='grid content-start gap-4 sm:grid-cols-2 lg:col-span-7'>
                        {INCLUDED.map(item => (
                            <li key={item} className='flex items-start gap-3 rounded-2xl border border-border bg-card p-5'>
                                <span className='flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'>
                                    <Check className='size-4' aria-hidden='true' />
                                </span>

                                <span className='pt-0.5 font-medium leading-snug'>
                                    {item}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <CtaBanner
                title="Not sure which model is right?"
                description="Tell us how you want to live and we will recommend a layout, or design one from scratch around you."
                secondaryHref='/process'
                secondaryLabel="See how we build"
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ModelsPageProps> = async () => {
    try {
        const models = await getModels()

        return { props: { models }, revalidate: 60 }
    }
    catch (error) {
        console.error('Models could not be loaded:', error)

        return { props: { models: [] }, revalidate: 10 }
    }
}

export default ModelsPage
